//@flow

import type {Action} from '../actions/actions';
import * as actions from '../actions/actions';
import type {PlayerData, PlayerState, PlayerEndScreen} from '../store';
import {getDefaultVideo, getPlayingVideoById, videoDataToPlayerData, watchableToVideoData} from '../helpers';

let initial = window.initialVideo && watchableToVideoData(window.initialVideo);
let defaultVideo = initial && getDefaultVideo(initial);

const defaultEndScreen: PlayerEndScreen = {
  nextVideo: null,
  autoplayTimer: {
    timeLeft: 10,
  },
  autoplayCancelled: false,
}

const defaultPlayerData: PlayerData = {
  id: '',
  tpMediaId: 0,
  position: 0,
  autoplay: false
};

const defaultPlayerState: PlayerState = Object.assign({
  fetchingClips: false,
  fetchedClips: false,
  error: null,
  playerAction: 'STOP',
  container: initial || {
    fullLength: null,
    preview: null,
    clips: [],
  },
  currentlyPlaying: defaultVideo && videoDataToPlayerData(defaultVideo) || defaultPlayerData,
  livestreamOverride: false,
  hasFullLength: false,
  hasPreview: false,
  hasClips: false,
  isPreviewPlaying: false,
  clipBarState: {
    open: false,
    pageSize: 5,
    currentPage: 1,
    animating: false,
    animDirection: 'RIGHT'
  },
  moreVideoOverlayOpen: false,
  endScreenOpen: false,
  endScreen: defaultEndScreen,
}, window.initialPlayer || {});

let initialPlayerState = defaultPlayerState;

/**
 * These actions are for user interactions with respect to the video player.
 *
 * @param state
 * @param action
 * @returns {*}
 */
export default function player(state: PlayerState = initialPlayerState, action: Action): PlayerState {

    switch (action.type) {

      // case actions.PLAYER_PLAY:
      // case actions.PLAYER_PAUSE:
      // case actions.PLAYER_STOP:
      // case actions.PLAYER_SEEK:
      //   return state;

      case actions.AUTOPLAY_CANCEL: {
        let newEndScreen = Object.assign({}, state.endScreen, {autoplayCancelled:true});
        return Object.assign(
          {},
          state, {
            endScreen: newEndScreen
          });
      }

      case actions.AUTOPLAY_TIMER_TICK:
        // should take number that is amount of time left (video time minus ticks)?
        // store has info on timer progress.
        let timer = state.endScreen.autoplayTimer;
        timer.timeLeft -= 1;
        let newEndScreen = Object.assign({}, state.endScreen);
        newEndScreen.autoplayTimer = timer;
        return Object.assign(
          {},
          state, {
            endScreen: newEndScreen
          });

      case actions.SET_POSITION:
        let newCurrentData = Object.assign(
          {},
          state.currentlyPlaying, {
            position: action.payload
          }
        );
        return Object.assign(
          {},
          state, { currentlyPlaying: newCurrentData }
        );

      case actions.FETCHING_CLIPS_DATA:
        return Object.assign(
          {},
          state, {fetchingClips: true, fetchedClips: false, error: null}
        );

      case actions.FETCHING_CLIPS_DATA_ERROR:
        return Object.assign(
          {},
          state, {fetchingClips: false, fetchedClips: false, error: action.payload}
        );

      case actions.RECEIVED_CLIPS_DATA:
        let playerContainer =  Object.assign({}, state.container, {clips: action.payload});
        let hasClipsFlag = (playerContainer.clips.length > 0);
        return Object.assign(
          {},
          state,
          {
            container: playerContainer,
            hasClips: hasClipsFlag,
            fetchedClips: true,
            fetchingClips: false,
            error: null,
          }
        );

      case actions.CLICK_CLIPS_ON:
        let newBarState = Object.assign({}, state.clipBarState, {open: true});
        return Object.assign(
          {},
          state, {
            clipBarState: newBarState
          }
        );

      case actions.CLICK_CLIPS_OFF: {
        let newBarState = Object.assign({}, state.clipBarState, {open: false});
        return Object.assign(
          {},
          state, {
            clipBarState: newBarState
          }
        );
      }

      case actions.CLICK_CLIP_L_ARROW:
        return Object.assign(
          {},
          state,
          { clipBarState: Object.assign({}, state.clipBarState, { animating: true, animDirection: 'LEFT' }) }
        );

      case actions.CLICK_CLIP_R_ARROW:
        return Object.assign(
          {},
          state,
          { clipBarState: Object.assign({}, state.clipBarState, { animating: true, animDirection: 'RIGHT' }) }
        );

      case actions.CLIP_CHANGE_PAGE:
        return Object.assign(
          {},
          state,
          { clipBarState: Object.assign({}, state.clipBarState, { currentPage: action.payload, animating: false }) }
        );

      case actions.CLIP_CHANGE_PAGE_SIZE:
        return Object.assign(
          {},
          state,
          { clipBarState: Object.assign({}, state.clipBarState, { pageSize: action.payload }) }
        );

      case actions.CLIP_VIDEO_END:
        let video = getPlayingVideoById(state.container, action.payload);

        if (!video) {
          return state;
        }

        let autoplay = false;

        if (state.container.fullLength && state.container.fullLength !== null) {
          autoplay = video.id !== state.container.fullLength.id;
        }

        return Object.assign(
          {},
          state, { currentlyPlaying: videoDataToPlayerData(video, autoplay) }
        );

      case actions.LOAD_VIDEO: {
        let newState = Object.assign(
          {},
          state,
          { currentlyPlaying: action.payload,
            endScreenOpen: false,
            endScreen: defaultEndScreen,
            livestreamOverride: false
          }
        );

        // If the video to play is a clip, then open the clip bar
        if (state.container.clips.map(c => c.id).indexOf(action.payload.id) > -1) {
          newState = Object.assign(
            newState,
            {
              clipBarState: Object.assign(
                {},
                state.clipBarState,
                {open: true}
              )
            }
          )
        }

        return newState;
      }

      case actions.CLICK_PREVIEW_ON:
        if (!state.container.preview) {
          return state;
        }

        return Object.assign(
          {},
          state,
          {
            currentlyPlaying: videoDataToPlayerData(state.container.preview, true),
            isPreviewPlaying: true,
            endScreenOpen: false,
            endScreen: defaultEndScreen,
            livestreamOverride: false
          }
        );

      case actions.CLICK_PREVIEW_OFF:
        if (!state.container.fullLength) {
          return state;
        }

        return Object.assign(
          {},
          state,
          {
            currentlyPlaying: videoDataToPlayerData(state.container.fullLength),
            isPreviewPlaying: false,
            endScreenOpen: false,
            endScreen: defaultEndScreen,
            livestreamOverride: false
          }
        );

      case actions.CLOSE_END_SCREEN: {
        return Object.assign(
          {},
          state,
          {
            endScreenOpen: false
          });
      }

      case actions.PREVIEW_VIDEO_END: {
        let video = getPlayingVideoById(state.container, action.payload);

        if (!video) {
          return state;
        }

        return Object.assign(
          {},
          state,
          {
            currentlyPlaying: videoDataToPlayerData(video, false),
            isPreviewPlaying: false,
            endScreenOpen: false,
            endScreen: defaultEndScreen,
            livestreamOverride: false
          }
        );
      }

      case actions.CLICK_ON_VIDEO:
        if (!state.container.fullLength) {
          return state;
        }

        return Object.assign(
          {},
          state, {
            currentlyPlaying: videoDataToPlayerData(state.container.fullLength),
            moreVideoOverlayOpen: false
          }
        );

      case actions.VIDEO_END: {
        let newEndScreen = Object.assign({}, state.endScreen);

        newEndScreen.nextVideo = action.payload;
        newEndScreen.autoplayTimer = { timeLeft: 10 };
        newEndScreen.autoplayCancelled = false;

        return Object.assign(
          {},
          state,
          {
            endScreen: newEndScreen,
            endScreenOpen: true
          });
      }

      case actions.CLICK_DESCRIPTION_MORE:
        return Object.assign(
          {},
          state,
          {
            moreVideoOverlayOpen: true
          }
        );

      case actions.CLICK_DESCRIPTION_CLOSE:
        return Object.assign(
          {},
          state,
          {
            moreVideoOverlayOpen: false
          }
        );

      case actions.RECEIVED_VIDEO_DATA:

        let container = {
          fullLength: action.payload.fullLength,
          preview: action.payload.preview,
          clips: action.payload.clips
        };

        let defaultVideo = getDefaultVideo(container);

        return Object.assign(
          {},
          state,
          {
            container: container,
            currentlyPlaying: {
              id: defaultVideo && defaultVideo.id || '',
              tpMediaId: defaultVideo && defaultVideo.tpMediaId || 0,
              position: 0,
              autoplay: false,
            },
            endScreenOpen: false,
          }
        );

      case actions.LOAD_LIVESTREAM: {
        let newBarState = Object.assign({}, state.clipBarState, {open: false});

        return Object.assign(
          {},
          state,
          {
            clipBarState: newBarState,
            livestreamOverride: action.payload
          }
        );
      }

      default:
        return state;
    }

  };